import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MessageSquare, Mail, AlertCircle } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function CreateTask() {
  const navigate = useNavigate();
  const [assignable, setAssignable] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", assigned_to: "", department_id: "", priority: "medium",
    due_date: "", due_time: "", category: "Other", tags: "", expected_completion_date: "",
    recurring_enabled: false, recurring_frequency: "daily",
    notify_whatsapp: false, notify_email: false,
  });

  useEffect(() => {
    api.get("/assignable-users").then((r) => setAssignable(r.data)).catch(() => {});
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const assignee = assignable.find((u) => u.id === form.assigned_to);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.assigned_to) {
      toast.error("Title and assignee are required");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/tasks", {
        title: form.title, description: form.description, assigned_to: form.assigned_to,
        department_id: form.department_id || null, priority: form.priority,
        due_date: form.due_date || null, due_time: form.due_time || null,
        category: form.category,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        expected_completion_date: form.expected_completion_date || null,
        recurring: {
          enabled: form.recurring_enabled,
          frequency: form.recurring_enabled ? form.recurring_frequency : "none",
          interval_days: null,
        },
        notify_whatsapp: form.notify_whatsapp,
        notify_email: form.notify_email,
      });
      const notifs = res.data.notifications || [];
      if (notifs.length) {
        notifs.forEach((n) => {
          if (n.status === "sent") toast.success(`${n.channel} notification sent to ${n.recipient_name}`);
          else if (n.status === "simulated") toast.info(`${n.channel} notification simulated for ${n.recipient_name} (provider in mock mode)`);
          else toast.error(`${n.channel} notification failed: ${n.failure_reason || "delivery error"}`);
        });
      }
      toast.success("Task created and assigned");
      navigate(`/tasks/${res.data.task.id}`);
    } catch (err) {
      toast.error(err.friendlyMessage);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl animate-fade-in" data-testid="create-task-page">
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Task Management</div>
      <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-900">Create Task</h1>

      <form onSubmit={submit} className="mt-6 space-y-6">
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h3 className="mb-4 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Task Information</h3>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="title">Task Title *</Label>
              <Input id="title" data-testid="create-task-title" value={form.title} onChange={(e) => set("title", e.target.value)}
                placeholder="e.g. Follow up with ABC Distributors" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="desc">Detailed Description / Instructions</Label>
              <Textarea id="desc" data-testid="create-task-description" rows={4} value={form.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Complete instructions, context and expected outcome..." />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                  <SelectTrigger data-testid="create-task-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="create-task-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Department</Label>
                <Select value={form.department_id} onValueChange={(v) => set("department_id", v)}>
                  <SelectTrigger data-testid="create-task-department"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Tags <span className="text-slate-400">(comma separated)</span></Label>
              <Input data-testid="create-task-tags" value={form.tags} onChange={(e) => set("tags", e.target.value)}
                placeholder="distributor, urgent, q2" />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h3 className="mb-4 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Assignment & Schedule</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Assign To *</Label>
              <Select value={form.assigned_to} onValueChange={(v) => set("assigned_to", v)}>
                <SelectTrigger data-testid="create-task-assignee"><SelectValue placeholder="Select a team member..." /></SelectTrigger>
                <SelectContent>
                  {assignable.map((u) => (
                    <SelectItem key={u.id} value={u.id} data-testid={`create-task-assignee-${u.id}`}>
                      {u.name} - {u.designation || u.role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">
                Only people you are permitted to assign to (by hierarchy and permissions) appear here.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Due Date</Label>
              <Input data-testid="create-task-due-date" type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Due Time <span className="text-slate-400">(optional)</span></Label>
              <Input data-testid="create-task-due-time" type="time" value={form.due_time} onChange={(e) => set("due_time", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Expected Completion Date</Label>
              <Input data-testid="create-task-expected-date" type="date" value={form.expected_completion_date}
                onChange={(e) => set("expected_completion_date", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Recurring Task</Label>
              <div className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5">
                <Checkbox data-testid="create-task-recurring" checked={form.recurring_enabled}
                  onCheckedChange={(v) => set("recurring_enabled", !!v)} />
                <span className="text-sm text-slate-700">Repeat</span>
                {form.recurring_enabled && (
                  <Select value={form.recurring_frequency} onValueChange={(v) => set("recurring_frequency", v)}>
                    <SelectTrigger data-testid="create-task-recurring-frequency" className="h-8 w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="create-task-notify-section">
          <h3 className="mb-1 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Notify Assignee</h3>
          <p className="mb-4 text-xs text-slate-400">
            Sent immediately after the task is assigned. Delivery is tracked separately from task assignment.
          </p>
          {!assignee && <p className="text-sm text-slate-400">Select an assignee to see available contact channels.</p>}
          {assignee && (
            <div className="space-y-3">
              <label className={`flex items-start gap-3 rounded-lg border p-3.5 transition-colors ${
                !settings?.whatsapp_enabled || !assignee.mobile ? "cursor-not-allowed border-slate-100 bg-slate-50 opacity-60" : "cursor-pointer border-slate-200 hover:border-teal-300"}`}>
                <Checkbox data-testid="create-task-whatsapp-checkbox" checked={form.notify_whatsapp}
                  disabled={!settings?.whatsapp_enabled || !assignee.mobile}
                  onCheckedChange={(v) => set("notify_whatsapp", !!v)} className="mt-0.5" />
                <span className="flex-1">
                  <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <MessageSquare className="h-4 w-4 text-teal-600" /> Send WhatsApp notification
                  </span>
                  {assignee.mobile
                    ? <span className="text-xs text-slate-500">To: {assignee.mobile}</span>
                    : <span className="flex items-center gap-1 text-xs text-rose-600"><AlertCircle className="h-3 w-3" /> No WhatsApp number on file</span>}
                  {!settings?.whatsapp_enabled && <span className="block text-xs text-rose-600">Disabled by company policy</span>}
                </span>
              </label>
              <label className={`flex items-start gap-3 rounded-lg border p-3.5 transition-colors ${
                !settings?.email_enabled || !assignee.email ? "cursor-not-allowed border-slate-100 bg-slate-50 opacity-60" : "cursor-pointer border-slate-200 hover:border-teal-300"}`}>
                <Checkbox data-testid="create-task-email-checkbox" checked={form.notify_email}
                  disabled={!settings?.email_enabled || !assignee.email}
                  onCheckedChange={(v) => set("notify_email", !!v)} className="mt-0.5" />
                <span className="flex-1">
                  <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <Mail className="h-4 w-4 text-primary" /> Send Email notification
                  </span>
                  {assignee.email
                    ? <span className="text-xs text-slate-500">To: {assignee.email}</span>
                    : <span className="flex items-center gap-1 text-xs text-rose-600"><AlertCircle className="h-3 w-3" /> No email on file</span>}
                  {!settings?.email_enabled && <span className="block text-xs text-rose-600">Disabled by company policy</span>}
                </span>
              </label>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate("/tasks")}>Cancel</Button>
          <Button data-testid="create-task-submit" type="submit" disabled={busy} className="bg-primary px-8 font-semibold hover:bg-[#1E3A8A]">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create & Assign Task
          </Button>
        </div>
      </form>
    </div>
  );
}
